import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import {
  RuleReferenceTypes,
  RuleResultStatuses,
} from '~/common/constants/api-ids';
import { typedApiClient } from '~/common/util/api-client';
import { getKeyByValue } from '~/common/util/object';
import type { GutterItem } from '~/types';

function mapGutterItem(
  item: Awaited<
    ReturnType<typeof typedApiClient.v1.loanGetNavigationDetails>
  >['data']['topNavigationItems'][number]['navigationGutterItems'][number],
  parent: GutterItem | null = null,
): GutterItem {
  if (!item) {
    // Classify is broken....
    return {
      additionalReferences: {},
    } as GutterItem;
  }

  const {
    ruleReferenceTypeId,
    ruleReferenceTypeDesc,
    ruleReferenceId,
    ruleReferenceLDId,
    ruleResultStatusId,
    additionalReferences,
    gutterItemChildren,
    ...rest
  } = item;

  const status = getKeyByValue(RuleResultStatuses, ruleResultStatusId);
  const ruleReferenceType = getKeyByValue(
    RuleReferenceTypes,
    ruleReferenceTypeId,
  );

  const result = {
    ...rest,
    status,
    ruleReference:
      ruleReferenceId && ruleReferenceType
        ? {
            type: ruleReferenceType,
            desc: ruleReferenceTypeDesc,
            lenderDatabaseId: ruleReferenceLDId,
            id: ruleReferenceId,
          }
        : null,
    additionalReferences:
      additionalReferences as GutterItem['additionalReferences'],
    children: [],
    parent,
  };
  result.children = (gutterItemChildren ?? []).map((item) =>
    mapGutterItem(item, result),
  );
  return result;
}

// Temporary shims in place of real implementation
export function useNavigation() {
  const home = useSelector(({ home }) => home);

  return {
    categories: home.navigationItems.categories,
    allAlerts: useMemo(
      () =>
        home.navigationItems.allAlerts.map((x) => mapGutterItem(x as unknown)),
      [home.navigationItems.allAlerts],
    ),
    allRequiredRulesCompleted: home.allRequiredRulesCompleted,
    selectedAlert: useSelectedAlert(),
    selectedCategory: home.navigationItems.categories.find(
      (cat) => cat.navigationCategoryId === home.selectedCategory,
    ),
    isLastAlert: home.isLastAlert,
  };
}

export function useSelectedAlert<U = GutterItem | null>(
  selector: (state: GutterItem | null) => U = (state) => state as U,
) {
  const selectedAlert = useSelector(({ home }) => home.selectedAlert);
  const parent = useSelector(({ home }) => home.selectedAlertParent);

  return useMemo(
    () =>
      selector(
        mapGutterItem(
          selectedAlert as unknown,
          parent && mapGutterItem(parent as unknown),
        ),
      ),
    // eslint-disable-next-line @eslint-react/exhaustive-deps
    [selectedAlert, parent],
  );
}

export function useLoanLockInformation() {
  const loanLockInformation = useSelector(
    ({ home }) => home.loanLockInformation,
  );

  return (
    loanLockInformation || {
      isLoanCurrentlyLocked: false,
      loanLockedBy: null,
    }
  );
}
